import React, { useState, useRef, useEffect, useMemo } from 'react';
import useAdminNotifications, { markNotificationsRead } from '../hooks/useAdminNotifications';
import { hasAccessToView } from '../constants/teamAccessPermissions';

const typeIcon = (type) => {
  if (type === 'order') return 'fa-shopping-cart';
  if (type === 'user') return 'fa-user-plus';
  if (type === 'stock') return 'fa-exclamation-triangle';
  return 'fa-bell';
};

const formatTime = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  const diff = Date.now() - d.getTime();
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/**
 * Header bell: live-ish feed (polling) of orders, users, low stock.
 * Low-stock opens storefront product in a new tab; orders/users navigate inside admin.
 */
const AdminNotificationsBell = ({ currentUser, onNavigate }) => {
  const isTeam = currentUser?.role === 'team';
  const access = currentUser?.accessPermissions;

  const canSee = (view) => !isTeam || hasAccessToView(view, access, true);

  const { items, unreadCount, loading, error, refetch, refreshUnreadFromStorage } = useAdminNotifications(
    currentUser,
    true
  );

  const visibleItems = useMemo(() => {
    return items.filter((it) => {
      if (it.openInNewTab && it.href) {
        return !isTeam || hasAccessToView('products', access, true);
      }
      const v = it.navigate?.view;
      if (!v) return false;
      return !isTeam || hasAccessToView(v, access, true);
    });
  }, [items, isTeam, access]);

  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      markNotificationsRead();
      refreshUnreadFromStorage();
      refetch();
    }
  };

  const onItemClick = (it) => {
    if (it.openInNewTab && it.href) {
      if (!canSee('products')) return;
      window.open(it.href, '_blank', 'noopener,noreferrer');
      markNotificationsRead();
      refreshUnreadFromStorage();
      setOpen(false);
      return;
    }
    const { view, params } = it.navigate || {};
    if (!view || !canSee(view)) return;
    markNotificationsRead();
    onNavigate(view, params || {});
    refreshUnreadFromStorage();
    setOpen(false);
  };

  const badge = unreadCount > 99 ? '99+' : String(unreadCount || '');

  return (
    <div className="admin-notif-wrap" ref={wrapRef}>
      <button
        type="button"
        className="action-btn admin-notif-trigger"
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="true"
        title="Notifications"
      >
        <i className="fas fa-bell" />
        {unreadCount > 0 && <span className="notification-badge admin-notif-badge">{badge}</span>}
      </button>

      {open && (
        <div className="admin-notif-dropdown" role="menu">
          <div className="admin-notif-dropdown-header">
            <span>Notifications</span>
            <button type="button" className="admin-notif-refresh" onClick={() => refetch()} title="Refresh">
              <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`} />
            </button>
          </div>
          <div className="admin-notif-list">
            {error && <div className="admin-notif-error">{error}</div>}
            {!error && visibleItems.length === 0 && !loading && (
              <div className="admin-notif-empty">No recent activity</div>
            )}
            {visibleItems.map((it) => (
              <button
                key={it.key}
                type="button"
                className="admin-notif-item"
                role="menuitem"
                onClick={() => onItemClick(it)}
              >
                <span className={`admin-notif-item-icon admin-notif-type-${it.type}`}>
                  <i className={`fas ${typeIcon(it.type)}`} />
                </span>
                <span className="admin-notif-item-body">
                  <span className="admin-notif-item-title">{it.title}</span>
                  <span className="admin-notif-item-sub">{it.subtitle}</span>
                </span>
                <span className="admin-notif-item-time">{formatTime(it.at)}</span>
              </button>
            ))}
          </div>
          <div className="admin-notif-dropdown-footer">
            Refreshes when you open this menu or return to the tab
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNotificationsBell;
